import { useVendorContext } from "@client-shared/providers/vendor";
import { zodResolver } from "@hookform/resolvers/zod";
import { FEATURE_FLAG_NAMES, type FormVariant } from "@shared/types";
import { isIntensityPosition } from "@shared/types/intensity";
import * as React from "react";
import { useForm, useFormState, useWatch, type FieldPath, type Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Accordion } from "@bcc/components/ui/accordion";
import { Checkbox } from "@bcc/components/ui/checkbox";
import { FileUpload } from "@bcc/components/ui/file-upload";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@bcc/components/ui/form";
import { Input } from "@bcc/components/ui/input";
import { MultiSelect, type MultiSelectOption } from "@bcc/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { Textarea } from "@bcc/components/ui/textarea";
import { useFeatureFlagsContext } from "@bcc/contexts/feature-flags-context";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import { homeRegionForVendor } from "@bcc/lib/products/cross-border-tax";
import {
  DEFAULT_DESCRIPTION_MAX_LENGTH,
  DEFAULT_MAX_ELEVATION,
  DEFAULT_MIN_ELEVATION,
  DEFAULT_MIN_ELEVATION_STEP,
  DEFAULT_ROASTER_NOTE_MAX_LENGTH,
  DEFAULT_TITLE_MAX_LENGTH,
  MAX_BADGES_SELECTION,
  NO_CHARACTER_VALUE,
} from "../constants";
import { createImportedProductSchema, createProductSchema, type ProductFormData } from "../schemas/product-schema";
import { deriveProductShipTo, isProductLevelLocked as isProductLevelLockedFn, mapWholeBeanVariants } from "../utils";
import { handleFilterTranslatedToConfig } from "../utils/handle-filter-translated-to-config";
import { VariantForm } from "../variant-form";
import { intensityMatchTokens } from "@bcc/components/views/products-view.utils";
import { IntensitySection } from "../intensity-section";
import { ErrorSummary } from "./error-summary";
import { ProductFormErrorPills, type SectionId } from "./product-form-error-pills";
import { ProductFormLiveDataWrapper } from "./product-form-live-data-wrapper";
import { ProductFormSection } from "./product-form-section";
import { deriveProductFormStatus } from "./product-form-status";
import type { ProductFormProps } from "./product-form.types";
import { mapProductToFormDefaults, normaliseIntensityFields, setProductFormTestId } from "./product-form.utils";

export function ProductForm(props: Readonly<ProductFormProps>) {
  const {
    product,
    onChange,
    onValidationChange,
    onStatusChange,
    productConfig,
    localisedProductConfig,
    titleMaxLength = DEFAULT_TITLE_MAX_LENGTH,
    productDescriptionMaxLength = DEFAULT_DESCRIPTION_MAX_LENGTH,
    roasterNoteMaxLength = DEFAULT_ROASTER_NOTE_MAX_LENGTH,
    isSingleBagSizeEnabled = false,
    shortTermMaxWeeklyInventory,
    shortTermMinWeeklyInventory,
    longTermMinWeeklyInventory,
    longTermMaxWeeklyInventory,
    regionLargeBagSize,
    marginFloorPercent,
    index,
    imageOptional = false,
    readOnlyProductFields = false,
    isCoffeeOnPim = false,
    allowAddRemoveVariants = false,
    comparisonCoffees = [],
    titleError,
    excludedBagSizes,
  } = props;

  const { t } = useTranslation();
  const [{ currentVendor }] = useVendorContext();
  const { isFeatureEnabled } = useFeatureFlagsContext();
  const isIntensityScoreEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.intensityScore);

  // The roaster's home region drives the editable side of cross-border per-bag
  // pricing. The vendor's SFDC `countryCode` is authoritative (an NL roaster can
  // sit under the "BDE" region), falling back to the region slice
  // ("BDE" → "DE"); undefined for non-cross-border vendors so variants keep the
  // single-price grid.
  const crossBorderHomeRegion = React.useMemo(
    () => homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region),
    [currentVendor?.countryCode, currentVendor?.region]
  );

  // Cross-border "Ship to" options come from the vendor's `properties.shipTo`
  // (e.g. ["NL", "DE"]). Non-cross-border vendors have none, so the field is
  // hidden for them — mirrors the products table's `showShipToColumn` gate. The
  // roaster's own home country is always offered and locked-on (`isDisabled`),
  // so a cross-border SKU can never end up with zero ship-to destinations.
  const shipToOptions: MultiSelectOption[] = React.useMemo(() => {
    const codes = currentVendor?.properties?.shipTo ?? [];
    if (codes.length < 2) return [];
    const withHome = crossBorderHomeRegion && !codes.includes(crossBorderHomeRegion) ? [crossBorderHomeRegion, ...codes] : codes;
    return withHome.map((countryCode) => ({
      label: countryCode,
      value: countryCode,
      isDisabled: countryCode === crossBorderHomeRegion,
    }));
  }, [currentVendor?.properties?.shipTo, crossBorderHomeRegion]);

  // These option lists derive only from `productConfig` / `localisedProductConfig`,
  // never from the field's own value, so memoizing them avoids re-filtering and
  // handing a new array reference to Select/MultiSelect on every keystroke in
  // that field (MultiSelect keys its own internal useMemos off `options` by reference).
  const mainFlavorCategoryOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.flavorCategories, "flavorCategories", productConfig),
    [localisedProductConfig.flavorCategories, productConfig]
  );
  const secondaryFlavorCategoryOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.secondaryFlavourCategories, "secondaryFlavourCategories", productConfig),
    [localisedProductConfig.secondaryFlavourCategories, productConfig]
  );
  const productTypeOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.productType, "productType", productConfig),
    [localisedProductConfig.productType, productConfig]
  );
  const roastLevelOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.roastLevel, "roastLevel", productConfig),
    [localisedProductConfig.roastLevel, productConfig]
  );
  const processOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.process, "process", productConfig),
    [localisedProductConfig.process, productConfig]
  );
  const varietalOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.varietal, "varietal", productConfig),
    [localisedProductConfig.varietal, productConfig]
  );
  // Tasting notes' `isDisabled` depends on the field's own current selection, so
  // only the config-filtered base list (the expensive part) is memoized here;
  // the per-render `isDisabled` overlay stays inline where the field value lives.
  const tastingNotesBaseOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.tastingNotes, "tastingNotes", productConfig),
    [localisedProductConfig.tastingNotes, productConfig]
  );
  const brewMethodOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.brewMethod, "brewMethod", productConfig),
    [localisedProductConfig.brewMethod, productConfig]
  );
  const certificationsOptions = React.useMemo(
    () => handleFilterTranslatedToConfig(localisedProductConfig.certifications, "certifications", productConfig),
    [localisedProductConfig.certifications, productConfig]
  );

  // Per-region VAT/KS rates from the tax-configuration API (static default until
  // the TaxRatesProvider is mounted, e.g. in isolated tests/stories).
  const taxRatesByRegion = useTaxRatesContext();

  const isBadgesEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.badges);
  // Character is raw Salesforce picklist strings (no PIM translation category), shipped behind the Badges flag.
  const characterOptions = React.useMemo(
    () => (Array.isArray(productConfig.character) ? productConfig.character : []),
    [productConfig.character]
  );

  const inventoryConfig = React.useMemo(
    () => ({
      shortTermMinWeeklyInventory,
      shortTermMaxWeeklyInventory,
      longTermMinWeeklyInventory,
      longTermMaxWeeklyInventory,
      imageOptional,
      marginFloorPercent,
    }),
    [
      shortTermMinWeeklyInventory,
      shortTermMaxWeeklyInventory,
      longTermMinWeeklyInventory,
      longTermMaxWeeklyInventory,
      imageOptional,
      marginFloorPercent,
    ]
  );

  // `titleMaxLength` is threaded into the schema (not just the character counter)
  // so the rendered "x/y" and the zod rule can never disagree.
  // `readOnlyProductFields` has to reach the resolver too: it renders every
  // product-level input disabled, so validating their content would hold
  // `standaloneIsValid` false with no field the roaster is allowed to fix.
  const schemaConfig = React.useMemo(
    () => ({ ...inventoryConfig, titleMaxLength, readOnlyProductFields, badgesEnabled: isBadgesEnabled }),
    [inventoryConfig, titleMaxLength, readOnlyProductFields, isBadgesEnabled]
  );

  // Use imported schema when product-level fields are locked — they may contain
  // data the user cannot edit, so only variant fields are strictly validated.
  // This covers both PIM-imported (localVariants) and SF-imported (isImportedRemote) products.
  const productSchemaWithConfig = React.useMemo(
    () => (isProductLevelLockedFn(product) ? createImportedProductSchema(schemaConfig) : createProductSchema(schemaConfig)),
    [schemaConfig, product]
  );

  // Initialize internal form instance for standalone mode
  // Both createProductSchema and createImportedProductSchema validate the same
  // form shape at runtime; the imported variant just relaxes product-level fields.
  // Cast the resolver to avoid a TS union mismatch between the two schema types.
  // Use "onChange" for locked products (imported/rejected) so every variant field
  // edit immediately updates isValid — "onBlur" never fires for disabled fields.
  const isLocked = isProductLevelLockedFn(product);
  const internalForm = useForm<ProductFormData>({
    resolver: zodResolver(productSchemaWithConfig) as Resolver<ProductFormData>,
    defaultValues: mapProductToFormDefaults(product),
    mode: isLocked ? "onChange" : "onBlur",
  });

  // Use external form if provided (integrated mode), otherwise use internal form (standalone mode)
  const form = props.form ?? internalForm;
  const isExternalForm = !!props.form;

  const { watch, setValue } = form;
  const externalFormState = useFormState({
    control: form.control,
  });

  // Standalone validity is computed directly from the schema (mirrors the
  // removed modal-level safeParse). RHF's `formState.isValid` under
  // `mode: "onBlur"` stays false for fully-prefilled products until a field is
  // blurred — that would leave a valid product unable to enable the batch
  // Import button. safeParse re-runs live on every change via useWatch and is
  // equivalent to the resolver (same schema), just without the blur lag.
  // Only run the schema safeParse in standalone mode. In integrated mode the
  // validity comes from `externalFormState.isValid`, so parsing here would be
  // wasted work on every change.
  const watchedValues = useWatch({ control: form.control });
  const standaloneIsValid = React.useMemo(
    () => (isExternalForm ? false : productSchemaWithConfig.safeParse(watchedValues).success),
    [isExternalForm, watchedValues, productSchemaWithConfig]
  );

  const isValid = isExternalForm ? externalFormState.isValid : standaloneIsValid;
  const isSubmitted = isExternalForm ? externalFormState.isSubmitted : form.formState.isSubmitted;

  // Compute if product has been imported locally
  const isProductImportedLocal = !!product.localVariants?.length;

  const isProductLevelLocked = React.useMemo(
    () => readOnlyProductFields || isProductLevelLockedFn(product),
    [readOnlyProductFields, product]
  );

  // Intensity is set once per coffee, so the section locks whenever the
  // coffee is already on PIM — the same condition that locks Badges and
  // Character — whether or not PIM carries a score yet. An unscored PIM
  // coffee stays locked and shows the preliminary notice until the beans
  // manager scores it. Only a coffee that has never been submitted keeps the
  // roaster's own provisional estimate editable.
  //
  // `isIntensityApproved` is narrower: it reports that a score exists (from
  // PIM or an approved sibling) and drives the notice and the ✓ in the scale.
  //
  // `product.hasApprovedIntensity`, when present, is computed by
  // `handleDisplayDraft` from the product *before* any draft was merged onto
  // it — required because a still-provisional draft position and an approved
  // PIM position are indistinguishable once merged into plain
  // `intensityPosition`. Fall back to deriving it from the current value for
  // products that never went through that merge — including the ShipStation
  // "New Bag Size" flow, whose PIM-only `existingProduct` carries no
  // `localVariants` (so `isProductLevelLockedFn` alone reads false) but is
  // still read-only-from-PIM, which `readOnlyProductFields` already signals.
  const isIntensityApproved =
    product.hasApprovedIntensity ??
    ((readOnlyProductFields || isProductLevelLockedFn(product)) && isIntensityPosition(product.intensityPosition));
  // Intensity is also set once per coffee at the first submit, so a coffee that
  // already has a Submitted/Pending variant in Salesforce is locked too, even
  // before it reaches PIM. Only a rejection reopens it (see
  // `isProductLevelLocked`).
  const isIntensityLocked = isProductLevelLocked || isProductImportedLocal || isCoffeeOnPim || isIntensityApproved;

  // Track available images separately from the form field. The form field
  // (imageUploads) stores the user's *selection* for validation, while this
  // state holds the full list of available thumbnails. Without this separation
  // the onChange callback merges the selected subset back into product, causing
  // the thumbnail list to collapse to the selection on the next render.
  const [availableImages, setAvailableImages] = React.useState(product.imageUploads ?? []);

  // Sync when product prop changes (e.g. step navigation, draft reload).
  // Always set — including null/undefined → [] — so switching products
  // doesn't leave stale thumbnails from the previous product.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync available images when product prop changes (step navigation, draft reload)
    setAvailableImages(product.imageUploads ?? []);
  }, [product.imageUploads]);

  // Keep a ref in sync so the watch subscription always reads the latest
  // available images without needing it in the effect dependency array.
  const availableImagesRef = React.useRef(availableImages);
  React.useEffect(() => {
    availableImagesRef.current = availableImages;
  }, [availableImages]);

  // Track country options based on selected region
  const [countryOptions, setCountryOptions] = React.useState<Array<{ label: string; value: string }>>([]);

  // Watch region field and update country options when it changes
  const selectedRegion = watch("region");

  React.useEffect(() => {
    if (selectedRegion && localisedProductConfig.regionAndCountry?.[selectedRegion]) {
      const options = localisedProductConfig.regionAndCountry[selectedRegion];
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync country options when selected region changes
      setCountryOptions(options);
      const currentCountry = form.getValues("country");
      if (currentCountry && !options.some((opt) => opt.value === currentCountry)) {
        setValue("country", "");
      }
    } else {
      setCountryOptions([]);
      setValue("country", "");
    }
  }, [selectedRegion, localisedProductConfig.regionAndCountry, setValue, form]);

  // Ship-to is bag-size based: it lives on the whole-bean variant and
  // is inherited by other grinds of the same bag size. The account-level option
  // list (`shipToOptions`, with the home country prepended + locked) is threaded
  // down to the variant form; per-variant seeding + the home-country lock now
  // happen there. The former product-level seeding effect (which defaulted a new
  // bag size to EVERY account destination — the bug) has been removed.

  // Watch form state and call onChange callback
  React.useEffect(() => {
    const subscription = watch((formData) => {
      if (onChange) {
        const variantsChanged = JSON.stringify(formData.variants ?? []) !== JSON.stringify(product.variants ?? []);

        // Merge form data back into product structure. Preserve the full
        // available-images list so the thumbnail grid isn't collapsed to the
        // user's current selection on the next render cycle.
        onChange({
          ...product,
          ...formData,
          ...normaliseIntensityFields(formData),
          imageUploads: availableImagesRef.current,
          hasVariantsDraft: variantsChanged || product.hasVariantsDraft || formData.hasVariantsDraft,
        } as ProductFormProps["product"]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, product]);

  // Watch validation state
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // Emit the derived neutral/progress/error/valid status for the batch-import
  // collapsible border.
  // `isDirty` is read off the ACTIVE form's root `formState` in both modes, not
  // the parallel `useFormState` subscription. RHF only computes `isDirty` when
  // something flags it on the *control*, and a `useFormState` proxy only flags
  // its own local subscription — so in integrated mode nothing ever asked the
  // control for it and `externalFormState.isDirty` stayed false forever (it was
  // likewise observed stale in standalone mode). Reading the root proxy opts the
  // control in, so this is the one source that is correct for both.
  const isDirty = form.formState.isDirty;
  const hasErrors = Object.keys(isExternalForm ? externalFormState.errors : form.formState.errors).length > 0;
  React.useEffect(() => {
    if (onStatusChange) {
      onStatusChange(deriveProductFormStatus({ isValid, isDirty, hasErrors }));
    }
  }, [isValid, isDirty, hasErrors, onStatusChange]);

  // The title is prefilled from the vendor system and is frequently never
  // focused, so under `mode: "onBlur"` its resolver never ran and an over-long
  // title produced no error, no red border and no message — the roaster only saw
  // an inert "49/40" counter. Validate `title` once the roaster first touches
  // *anything*, so the reason the card can't go green is visible.
  // Scoped to `title` on purpose: a bare `form.trigger()` would populate errors
  // for every still-empty required field on the first keystroke and flip an
  // in-progress card red before the roaster has had a chance to fill it in.
  const hasValidatedPrefilledTitleRef = React.useRef(false);
  React.useEffect(() => {
    if (!isDirty) {
      // A form.reset() (the SS onboarding modal does this when the product it
      // is editing changes) clears isDirty — re-arm so the next edit validates
      // the freshly prefilled title instead of silently skipping it.
      hasValidatedPrefilledTitleRef.current = false;
      return;
    }
    // Locked products render the title disabled, so an error here would point at
    // a field the roaster cannot edit. Their resolvers relax the product-level
    // rules to match (`readOnlyProductFields`), so there is no error to surface
    // and nothing holding the form invalid.
    if (isProductLevelLocked || hasValidatedPrefilledTitleRef.current) {
      return;
    }
    hasValidatedPrefilledTitleRef.current = true;
    // Only a title that actually arrived prefilled can be the never-focused
    // over-long title this exists to surface. The SS "new SKU" flow starts from
    // an empty title, where eagerly validating would flag "Product Title is
    // required" the moment the roaster types in any other field — the very
    // premature-error behaviour the scoped trigger is meant to avoid.
    if (!form.getValues("title")) {
      return;
    }
    void form.trigger("title");
  }, [isDirty, isProductLevelLocked, form]);

  // A product must not be a comparison point for itself: on the reopen /
  // already-imported path its own catalog entry is in the list, where it would
  // render as a marker on its own scale and be selectable in the "most similar
  // to" dropdown — producing an estimate against itself.
  //
  // Matched on the same identity tokens the comparison list was built from
  // (ids plus the case-normalised title) rather than an exact title compare: a
  // Shopify "Karibu" and a PIM "KARIBU" are the same coffee, and PIM copy can
  // be copy-edited or localised after import so the titles need not match at
  // all.
  const ownComparisonCoffees = React.useMemo(() => {
    const ownTokens = new Set(intensityMatchTokens(product));
    if (ownTokens.size === 0) return comparisonCoffees;
    return comparisonCoffees.filter((coffee) => !coffee.matchTokens.some((token) => ownTokens.has(token)));
  }, [comparisonCoffees, product]);

  // Intensity only makes sense once the beans manager has approved at least
  // one of this roaster's coffees in PIM — with nothing scored yet, an empty
  // scale and comparison dropdowns would just confuse a first-time importer.
  // Checked against the unfiltered vendor-wide list (not ownComparisonCoffees)
  // so a product that is itself the roaster's only approved coffee still
  // counts.
  const hasPimApprovedIntensity = React.useMemo(
    () => comparisonCoffees.some((coffee) => coffee.confidence === "approved"),
    [comparisonCoffees]
  );

  // Live PIM products (§13.3) start with sections 1 & 2 collapsed — the data
  // is already live and roasters usually only need to touch the variants.
  const [openSections, setOpenSections] = React.useState<string[]>(
    isProductImportedLocal ? ["intensity", "variants"] : ["details", "attributes", "intensity", "variants"]
  );

  const pillFocusTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  React.useEffect(() => () => clearTimeout(pillFocusTimeoutRef.current), []);

  const handlePillClick = React.useCallback(
    (fieldPath: string, section: SectionId) => {
      setOpenSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
      clearTimeout(pillFocusTimeoutRef.current);
      // Wait for the accordion open animation before focusing (Radix animates ~200ms)
      pillFocusTimeoutRef.current = setTimeout(() => {
        form.setFocus(fieldPath as FieldPath<ProductFormData>);
      }, 200);
    },
    [form]
  );

  // Cross-field validation trigger: When variants change after submission,
  // re-validate entire form including cross-field rules.
  const variants = watch("variants");
  React.useEffect(() => {
    if (isSubmitted) {
      void form.trigger(); // Re-validate entire form including cross-field rules
    }
  }, [variants, isSubmitted, form]);

  // Form submission handler (only used in standalone mode)
  const handleFormSubmit = form.handleSubmit(
    // On valid
    (data) => {
      // Pre-submission transformation: map whole bean variants (propagates
      // ship-to to same-bag-size grinds). Re-derive the product-level `ship_to`
      // from the whole-bean variant so the Shopify-import submission mapper —
      // which still reads a single product-level value — stays in sync now that
      // ship-to is bag-size based.
      const mappedVariants = mapWholeBeanVariants(data.variants as FormVariant[]);
      const transformedData = {
        ...data,
        variants: mappedVariants,
        ship_to: deriveProductShipTo(mappedVariants) ?? product.ship_to,
      };

      if (onChange) {
        onChange({
          ...product,
          ...transformedData,
          ...normaliseIntensityFields(transformedData),
        } as ProductFormProps["product"]);
      }
    },
    // On invalid - errors automatically display via ErrorSummary + inline FormMessage
    (errors) => {
      console.error("Form validation errors:", errors);
    }
  );

  // Form fields content (shared between standalone and integrated modes)
  const formFieldsContent = (
    <>
      {/* Error Summary - displays at top after submission if errors exist */}
      <ErrorSummary className="mb-6" />

      {/* Clickable error pills — one per validation error, visible as soon as the
          form is dirty. Clicking a pill opens the relevant section and focuses
          the offending field. */}
      <ProductFormErrorPills isDirty={isDirty} isSubmitted={isSubmitted} onPillClick={handlePillClick} />

      {/* All 3 sections are collapsible and start expanded. Controlled so pills
          can open a collapsed section before scrolling to the errored field. */}
      <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="space-y-4 bg-surface-2">
        <ProductFormLiveDataWrapper isLive={isProductImportedLocal} testId={setProductFormTestId(`live-data-wrapper-${index}`)}>
          {/* Section 1: name / description / roaster notes / images */}
          <ProductFormSection
            value="details"
            title={t("views.shopifyImportModal.configure.sections.details")}
            testId={setProductFormTestId(`section-details-${index}`)}
            triggerTestId={setProductFormTestId(`section-details-trigger-${index}`)}
            contentClassName="space-y-6 "
          >
            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("views.shopifyImportModal.productForm.labels.title")}{" "}
                    <span className="text-destructive" aria-hidden="true">
                      *
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("views.shopifyImportModal.productForm.placeholders.title")}
                      disabled={isProductLevelLocked}
                      testId={setProductFormTestId("title")}
                    />
                  </FormControl>
                  <div className="text-xs text-muted-foreground" data-testid={setProductFormTestId("title-counter")}>
                    {field.value.length}/{titleMaxLength}
                  </div>
                  <FormMessage />
                  {titleError && <p className="text-sm font-medium text-destructive">{titleError}</p>}
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("views.shopifyImportModal.productForm.labels.description")}{" "}
                    <span className="text-destructive" aria-hidden="true">
                      *
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t("views.shopifyImportModal.productForm.placeholders.description")}
                      rows={10}
                      disabled={isProductLevelLocked}
                      testId={setProductFormTestId("description")}
                    />
                  </FormControl>
                  <div className="text-xs text-muted-foreground">
                    {field.value.length}/{productDescriptionMaxLength}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Roaster Notes */}
            <FormField
              control={form.control}
              name="roasterNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("views.shopifyImportModal.productForm.labels.roasterNotes")}{" "}
                    <span className="text-destructive" aria-hidden="true">
                      *
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t("views.shopifyImportModal.productForm.placeholders.roasterNotes")}
                      rows={5}
                      disabled={isProductLevelLocked}
                      testId={setProductFormTestId("roaster-notes")}
                    />
                  </FormControl>
                  <div className="text-xs text-muted-foreground">
                    {field.value.length}/{roasterNoteMaxLength}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Image Uploads */}
            <FormField
              control={form.control}
              name="imageUploads"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("views.shopifyImportModal.productForm.labels.imageUploads")}{" "}
                    {!imageOptional && (
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <FileUpload
                      value={availableImages}
                      onChange={(nextValue) => {
                        setAvailableImages(nextValue);
                        // Clear selection when available images change (add/delete)
                        field.onChange(null);
                        void form.trigger("imageUploads");
                      }}
                      onSelectionChange={(selected) => {
                        field.onChange(selected.length > 0 ? selected : null);
                        void form.trigger("imageUploads");
                      }}
                      selectType="single"
                      uploadEnabled={true}
                      deleteEnabled={true}
                      disabled={isProductLevelLocked}
                      testId={setProductFormTestId(`file-upload-${index}`)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </ProductFormSection>

          {/* Section 2: varietal & remaining product-level attributes */}
          <ProductFormSection
            value="attributes"
            title={t("views.shopifyImportModal.configure.sections.attributes")}
            testId={setProductFormTestId(`section-attributes-${index}`)}
            triggerTestId={setProductFormTestId(`section-attributes-trigger-${index}`)}
            contentClassName="space-y-6"
          >
            {/* Flavor Categories and Decaf */}
            <div className="grid gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="mainFlavorCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("views.shopifyImportModal.productForm.labels.mainFlavorCategory")}{" "}
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("main-flavor-category")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {mainFlavorCategoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="secondaryFlavorCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("views.shopifyImportModal.productForm.labels.secondaryFlavorCategory")}{" "}
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("secondary-flavor-category")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {secondaryFlavorCategoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isDecaf"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-8">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isProductLevelLocked}
                        testId={setProductFormTestId("is-decaf")}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{t("views.shopifyImportModal.productForm.labels.isDecaf")}</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            {/* Product Type, Roast Level, Process */}
            <div className="grid gap-4 md:grid-cols-3">
              <FormField
                control={form.control}
                name="productType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("views.shopifyImportModal.productForm.labels.productType")}{" "}
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("product-type")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {productTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="roastLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("views.shopifyImportModal.productForm.labels.roastLevel")}{" "}
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("roast-level")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roastLevelOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="process"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.process")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("process")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {processOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Varietal and Tasting Notes */}
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="varietal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.varietal")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("varietal")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {varietalOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tasting Notes */}
              <FormField
                control={form.control}
                name="tastingNotes"
                render={({ field }) => {
                  const tastingNotes = field.value ?? [];

                  return (
                    <FormItem>
                      <FormLabel>
                        {t("views.shopifyImportModal.productForm.labels.tastingNotes")}{" "}
                        <span className="text-destructive" aria-hidden="true">
                          *
                        </span>
                      </FormLabel>
                      <div className="space-y-2">
                        <MultiSelect
                          options={tastingNotesBaseOptions.map((option) => ({
                            ...option,
                            isDisabled: !tastingNotes.includes(option.value) && tastingNotes.length >= 3,
                          }))}
                          value={tastingNotes}
                          onChange={field.onChange}
                          disabled={isProductLevelLocked}
                          placeholder={t("views.shopifyImportModal.productForm.placeholders.selectMultiple")}
                          searchPlaceholder={t("components.multiSelect.searchPlaceholder")}
                          emptyMessage={t("components.multiSelect.emptyMessage")}
                          clearAllText={t("components.multiSelect.clearAll")}
                          identifier={setProductFormTestId("tasting-notes")}
                        />
                        <div className="text-xs text-muted-foreground" data-testid={setProductFormTestId("tasting-notes-helper")}>
                          {t("views.shopifyImportModal.productForm.helpers.tastingNotesMax", { count: tastingNotes.length, max: 3 })}
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            {/* Brew Method and Certifications */}
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="brewMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("views.shopifyImportModal.productForm.labels.brewMethod")}{" "}
                      <span className="text-destructive" aria-hidden="true">
                        *
                      </span>
                    </FormLabel>
                    <div className="space-y-2">
                      <MultiSelect
                        options={brewMethodOptions}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isProductLevelLocked}
                        placeholder={t("views.shopifyImportModal.productForm.placeholders.selectMultiple")}
                        searchPlaceholder={t("components.multiSelect.searchPlaceholder")}
                        emptyMessage={t("components.multiSelect.emptyMessage")}
                        clearAllText={t("components.multiSelect.clearAll")}
                        identifier={setProductFormTestId("brew-method")}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="certifications"
                render={({ field }) => {
                  const certifications = field.value ?? [];

                  return (
                    <FormItem>
                      <FormLabel>{t("views.shopifyImportModal.productForm.labels.certifications")}</FormLabel>
                      <div className="space-y-2">
                        <MultiSelect
                          options={certificationsOptions}
                          value={certifications}
                          onChange={(values) => field.onChange(values.length > 0 ? values : null)}
                          disabled={isProductLevelLocked}
                          placeholder={t("views.shopifyImportModal.productForm.placeholders.selectMultiple")}
                          searchPlaceholder={t("components.multiSelect.searchPlaceholder")}
                          emptyMessage={t("components.multiSelect.emptyMessage")}
                          clearAllText={t("components.multiSelect.clearAll")}
                          identifier={setProductFormTestId("certifications")}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>

            {/* Badges */}
            {isBadgesEnabled && (
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="badges"
                  render={({ field }) => {
                    const badges = field.value ?? [];
                    // Badges are raw Salesforce strings with no PIM translation category (see
                    // LocalisedProductConfig), so they can't go through handleFilterTranslatedToConfig.
                    const badgeOptions = (Array.isArray(productConfig.badges) ? productConfig.badges : []).map((badge) => ({
                      label: badge,
                      value: badge,
                      isDisabled: !badges.includes(badge) && badges.length >= MAX_BADGES_SELECTION,
                    }));

                    return (
                      <FormItem>
                        <FormLabel>{t("views.shopifyImportModal.productForm.labels.badges")}</FormLabel>
                        <div className="space-y-2">
                          <MultiSelect
                            options={badgeOptions}
                            value={badges}
                            onChange={(values) => field.onChange(values.length > 0 ? values : null)}
                            disabled={isProductLevelLocked}
                            placeholder={t("views.shopifyImportModal.productForm.placeholders.selectMultiple")}
                            searchPlaceholder={t("components.multiSelect.searchPlaceholder")}
                            emptyMessage={t("components.multiSelect.emptyMessage")}
                            clearAllText={t("components.multiSelect.clearAll")}
                            identifier={setProductFormTestId("badges")}
                            testId={setProductFormTestId("badges")}
                          />
                          <div className="text-xs text-muted-foreground" data-testid={setProductFormTestId("badges-helper")}>
                            {t("views.shopifyImportModal.productForm.helpers.badgesMax", {
                              count: badges.length,
                              max: MAX_BADGES_SELECTION,
                            })}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="character"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("views.shopifyImportModal.productForm.labels.character")}</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value === NO_CHARACTER_VALUE ? "" : value)}
                        value={field.value ?? ""}
                      >
                        <FormControl>
                          <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("character")}>
                            <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NO_CHARACTER_VALUE}>
                            {t("views.shopifyImportModal.productForm.placeholders.noneOption")}
                          </SelectItem>
                          {characterOptions.map((character) => (
                            <SelectItem key={character} value={character}>
                              {character}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Region and Country */}
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.region")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("region")}>
                          <SelectValue placeholder={t("views.shopifyImportModal.productForm.placeholders.selectOption")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {localisedProductConfig.region.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.country")}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={countryOptions.length === 0 || isProductLevelLocked}
                    >
                      <FormControl>
                        <SelectTrigger disabled={isProductLevelLocked} testId={setProductFormTestId("country")}>
                          <SelectValue
                            placeholder={
                              countryOptions.length === 0
                                ? t("views.shopifyImportModal.productForm.placeholders.selectRegionFirst")
                                : t("views.shopifyImportModal.productForm.placeholders.selectOption")
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {countryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Ship to (cross-border) is bag-size based — rendered on
                the whole-bean variant inside the Variants section, not here. */}

            {/* Elevation */}
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="elevationStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.elevationStart")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={DEFAULT_MIN_ELEVATION}
                        max={DEFAULT_MAX_ELEVATION}
                        step={DEFAULT_MIN_ELEVATION_STEP}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        disabled={isProductLevelLocked}
                        testId={setProductFormTestId("elevation-start")}
                      />
                    </FormControl>
                    <div className="text-xs text-muted-foreground">
                      {t("views.shopifyImportModal.productForm.helpers.elevationRange", {
                        min: DEFAULT_MIN_ELEVATION,
                        max: DEFAULT_MAX_ELEVATION,
                      })}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="elevationEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("views.shopifyImportModal.productForm.labels.elevationEnd")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={DEFAULT_MIN_ELEVATION}
                        max={DEFAULT_MAX_ELEVATION}
                        step={DEFAULT_MIN_ELEVATION_STEP}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        disabled={isProductLevelLocked}
                        testId={setProductFormTestId("elevation-end")}
                      />
                    </FormControl>
                    <div className="text-xs text-muted-foreground">
                      {t("views.shopifyImportModal.productForm.helpers.elevationRange", {
                        min: DEFAULT_MIN_ELEVATION,
                        max: DEFAULT_MAX_ELEVATION,
                      })}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </ProductFormSection>
        </ProductFormLiveDataWrapper>
        {/* Section 3: Intensity. Deliberately outside ProductFormLiveDataWrapper
            so an already-imported product's still-provisional score stays
            editable and comparable against the rest of the line-up. Gated
            behind Feature_IntensityScore until the score is rolled out, and
            further hidden until the vendor has at least one PIM-approved
            score to build on. */}
        {isIntensityScoreEnabled && hasPimApprovedIntensity && (
          <ProductFormSection
            value="intensity"
            title={t("views.shopifyImportModal.configure.sections.intensity")}
            testId={setProductFormTestId(`section-intensity-${index}`)}
            triggerTestId={setProductFormTestId(`section-intensity-trigger-${index}`)}
            contentClassName="space-y-6"
          >
            {/* Also locked in the "New Bag Size" flow regardless of PIM state:
                the score belongs to the parent coffee, so editing it here would
                change it for the new bag size only and silently diverge from
                its siblings. */}
            <IntensitySection comparisonCoffees={ownComparisonCoffees} disabled={isIntensityLocked} isApproved={isIntensityApproved} />
          </ProductFormSection>
        )}
        {/* Section 4: Variants */}
        <ProductFormSection
          value="variants"
          title={t("views.shopifyImportModal.configure.sections.variants")}
          testId={setProductFormTestId(`section-variants-${index}`)}
          triggerTestId={setProductFormTestId(`section-variants-trigger-${index}`)}
        >
          <VariantForm
            productConfig={productConfig}
            localisedProductConfig={localisedProductConfig}
            isSingleBagSizeEnabled={isSingleBagSizeEnabled}
            isProductImportedLocal={isProductImportedLocal}
            shortTermMaxWeeklyInventory={shortTermMaxWeeklyInventory}
            shortTermMinWeeklyInventory={shortTermMinWeeklyInventory}
            longTermMinWeeklyInventory={longTermMinWeeklyInventory}
            longTermMaxWeeklyInventory={longTermMaxWeeklyInventory}
            regionLargeBagSize={regionLargeBagSize}
            allowAddRemoveVariants={allowAddRemoveVariants}
            excludedBagSizes={excludedBagSizes}
            homeRegion={crossBorderHomeRegion}
            taxRatesByRegion={taxRatesByRegion}
            shipToOptions={shipToOptions}
          />
        </ProductFormSection>
      </Accordion>
    </>
  );

  // In integrated mode: render as div (parent owns form element)
  // In standalone mode: render as form with submit handler
  return (
    <Form {...form}>
      {isExternalForm ? (
        <div className="space-y-6">{formFieldsContent}</div>
      ) : (
        <form className="space-y-6" onSubmit={handleFormSubmit}>
          {formFieldsContent}
        </form>
      )}
    </Form>
  );
}
